import type { UseFetchOptions } from "nuxt/app";
import * as Sentry from "@sentry/nuxt";
import { useConfig } from "~/composables/useConfig";

interface useApiClientOptions<O, T> extends UseFetchOptions<unknown> {
  mapper?: DataMapper<O, T>;
}

interface MockResponse<O> {
  data: O;
  delay?: number;
  success?: boolean;
  message?: string;
}

export const useApiClient = async <T, O = T>(
  url: string | (() => string),
  options: useApiClientOptions<O, T> = {},
  // A factory, not a value: an inline mock would run its fixture writes (and
  // import the fixtures) on every real API call too.
  mockResponse?: () => MockResponse<O> | Promise<MockResponse<O>>
): Promise<ResponseApi<T>> => {
  try {
    const runtimeConfig = useRuntimeConfig();

    if (
      mockResponse !== undefined &&
      runtimeConfig.public.MOCK_API_ENABLED !== false
    ) {
      const mock = await mockResponse();
      const delay = mock.delay ?? 0;
      const dataResponse = options.mapper
        ? options.mapper.map(mock.data)
        : (mock.data as unknown as T);
      const response = {
        data: dataResponse,
        error: undefined,
        success: mock.success ?? true,
        message: mock.message
      };

      if (delay > 0) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(response);
          }, delay);
        });
      }

      return response;
    }
    const { config } = useConfig();
    const { mapper, ...requestOptions } = options;
    const response = await $fetch<{
      data: O;
      message?: string;
      success: boolean;
    }>(typeof url === "string" ? url : url(), {
      baseURL: config.API_URL,
      credentials: "include",
      ...requestOptions
    } as Parameters<typeof $fetch>[1]);

    if (mapper) {
      const mappedData = mapper.map(response.data);
      return {
        data: mappedData,
        error: undefined,
        success: response.success,
        message: response.message
      };
    }
    return {
      data: response.data as unknown as T,
      error: undefined,
      success: response.success,
      message: response.message
    };
  } catch (err) {
    Sentry.captureException(err, {
      extra: {
        url: typeof url === "string" ? url : url(),
        method: options?.method
      }
    });
    return {
      data: undefined as unknown as T,
      error: {
        details: (err as any)?.data?.error?.details || (err as any)?.message,
        appCode: (err as any)?.data?.error?.code || "API_CLIENT_ERROR",
        context: {
          url: typeof url === "string" ? url : url(),
          method: options?.method as string | undefined,
          statusCode: (err as any)?.statusCode || 500
        }
      },
      success: false,
      message: (err as any)?.data?.message || "API request failed"
    };
  }
};
